/**
 * Preferences — theme, locale, notifications, accessibility, keyboard shortcuts.
 *
 * Autosaves on each change (debounced). Language/timezone PATCH to /users/me/;
 * all other prefs PATCH to /users/me/preferences/.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DARK_MODE_AVAILABLE, useSuiteChromeLayout } from "@weco/suite-chrome";
import { Toast } from "@weco/ui";
import { useTranslation } from "react-i18next";
import { ApiClientError } from "../api/client";
import { fetchMe, fetchPreferences, patchMe, patchPreferences } from "../api/preferences.api";
import type {
  UserAccessibilityFontSize,
  UserDateFormat,
  UserNotificationDigest,
  UserPreferences,
  UserStartOfWeek,
  UserTheme,
  UserTimeFormat,
} from "../api/types/preferences";
import { useAccessibility } from "../hooks/useAccessibility";
import { useDebouncedCallback } from "../hooks/useDebounce";

function isStoredSuiteTheme(t: UserTheme): t is "light" | "dark" | "system" {
  return t === "light" || t === "dark" || t === "system";
}

export const PreferencesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const formId = useId();
  const { setTheme } = useSuiteChromeLayout();

  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  const [userLang, setUserLang] = useState<string>("en-us");
  const [userTimezone, setUserTimezone] = useState<string>("UTC");
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "info" } | null>(
    null,
  );
  const themeAppliedRef = useRef(false);

  /* ── Accessibility CSS on <html> ────────────────────────────────────── */
  useAccessibility({
    reduceMotion: prefs?.accessibility_reduce_motion ?? false,
    highContrast: prefs?.accessibility_high_contrast ?? false,
    fontSize: prefs?.accessibility_font_size ?? "medium",
  });

  /* ── Load ────────────────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [prefsData, meData] = await Promise.all([fetchPreferences(), fetchMe()]);
      setPrefs(prefsData);
      setUserLang(meData.language ?? "en-us");
      setUserTimezone(meData.timezone ?? "UTC");
    } catch (e) {
      const msg = e instanceof ApiClientError ? e.message : t("preferences.loadFailed", { defaultValue: "Could not load preferences" });
      setError(msg);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once; `t` only shapes the error text
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Apply server theme once after first load (aligns chrome with People preferences). */
  useEffect(() => {
    if (!prefs || themeAppliedRef.current) return;
    themeAppliedRef.current = true;
    if (isStoredSuiteTheme(prefs.theme)) {
      setTheme(prefs.theme);
    }
  }, [prefs, setTheme]);

  /** Scroll to hash section (e.g. #keyboard-shortcuts). */
  useEffect(() => {
    const id = location.hash.replace(/^#/, "");
    if (!id) return;
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  /** Auto-clear "Saved" status after 3 seconds. */
  useEffect(() => {
    if (saveStatus !== "saved") return;
    const timer = setTimeout(() => setSaveStatus("idle"), 3000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  /* ── Autosave ───────────────────────────────────────────────────────── */

  // We use refs so the debounced callback always sees fresh state without
  // needing to be re-created on every state change.
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const langRef = useRef(userLang);
  langRef.current = userLang;
  const tzRef = useRef(userTimezone);
  tzRef.current = userTimezone;

  const performSave = useCallback(async () => {
    const p = prefsRef.current;
    if (!p) return;
    setSaveStatus("saving");
    try {
      const [updatedPrefs] = await Promise.all([
        patchPreferences({
          date_format: p.date_format,
          time_format: p.time_format,
          start_of_week: p.start_of_week,
          notifications_email: p.notifications_email,
          notifications_push: p.notifications_push,
          notifications_in_app: p.notifications_in_app,
          notification_digest: p.notification_digest,
          accessibility_reduce_motion: p.accessibility_reduce_motion,
          accessibility_high_contrast: p.accessibility_high_contrast,
          accessibility_font_size: p.accessibility_font_size,
          keyboard_shortcuts_enabled: p.keyboard_shortcuts_enabled,
        }),
        patchMe({ language: langRef.current, timezone: tzRef.current }),
      ]);
      setPrefs(updatedPrefs);
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
    }
  }, []);

  const { trigger: debouncedSave } = useDebouncedCallback(performSave, 800);

  const updateLocal = (partial: Partial<UserPreferences>) => {
    setPrefs((p) => (p ? { ...p, ...partial } : p));
    debouncedSave();
  };

  const handleLangChange = (val: string) => {
    setUserLang(val);
    debouncedSave();
  };

  const handleTimezoneChange = (val: string) => {
    setUserTimezone(val);
    debouncedSave();
  };

  /* ── Theme (saves immediately, no debounce) ─────────────────────────── */
  const handleThemeChange = async (next: UserTheme) => {
    if (!prefs) return;
    const prev = prefs.theme;
    updateLocal({ theme: next });
    if (isStoredSuiteTheme(next)) {
      setTheme(next);
    }
    try {
      const updated = await patchPreferences({ theme: next });
      setPrefs(updated);
    } catch (e) {
      updateLocal({ theme: prev });
      if (isStoredSuiteTheme(prev)) {
        setTheme(prev);
      }
      const msg = e instanceof ApiClientError ? e.message : t("preferences.themeFailed", { defaultValue: "Could not save theme" });
      setToast({ message: msg, type: "error" });
    }
  };


  const field = (key: string, defaultValue: string) => t(`preferences.${key}`, { defaultValue });

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar p-6 max-md:p-4 lg:p-12 bg-muted/10 h-full">
      <div className="max-w-3xl mx-auto w-full mt-4 lg:mt-8 space-y-8 max-md:space-y-5 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="text-start">
            <h1 className="text-3xl max-md:text-[22px] lg:text-4xl font-black tracking-tight">
              {field("title", "Preferences")}
            </h1>
            <p className="text-muted-foreground font-medium mt-2 max-md:text-[12.5px]">
              {field("subtitle", "Appearance, notifications, and accessibility")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground me-1" aria-live="polite">
              {saveStatus === "saving" && field("saving", "Saving…")}
              {saveStatus === "saved" && field("saved", "Saved")}
              {saveStatus === "error" && (
                <span className="text-red-600 dark:text-red-400">{field("saveFailed", "Could not save")}</span>
              )}
            </span>
            <button type="button" onClick={() => navigate("/account")} className={BTN_OUTLINE}>
              {field("accountSecurity", "Account & security")}
            </button>
            <button type="button" onClick={() => navigate("/profile")} className={BTN_OUTLINE}>
              {field("profile", "Profile")}
            </button>
          </div>
        </div>

        {loading && (
          <div
            className="space-y-8 animate-pulse"
            aria-busy="true"
            aria-label={field("loading", "Loading preferences")}
          >
            <div className={`${CARD} h-32`} />
            <div className={`${CARD} h-40`} />
          </div>
        )}

        {!loading && prefs && (
          <>
            {/* ── Language & Region ──────────────────────────────────── */}
            <section className={CARD}>
              <h2 className={H2}>{field("languageRegion", "Language & region")}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor={`${formId}-lang`} className={LABEL}>
                    {field("language", "Language")}
                  </label>
                  <select
                    id={`${formId}-lang`}
                    value={userLang}
                    onChange={(e) => handleLangChange(e.target.value)}
                    className={INPUT}
                  >
                    {/* Values are the backend User.language enum. */}
                    <option value="en-us">English</option>
                    <option value="fr-fr">Français</option>
                  </select>
                </div>
                <div>
                  <label htmlFor={`${formId}-tz`} className={LABEL}>
                    {field("timezone", "Timezone")}
                  </label>
                  <input
                    id={`${formId}-tz`}
                    type="text"
                    dir="ltr"
                    list={`${formId}-tz-list`}
                    value={userTimezone}
                    onChange={(e) => handleTimezoneChange(e.target.value)}
                    placeholder={field("timezoneSearch", "Search timezones…")}
                    className={INPUT}
                  />
                  <datalist id={`${formId}-tz-list`}>
                    {(
                      (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
                        .supportedValuesOf?.("timeZone") ?? []
                    ).map((tz: string) => (
                      <option key={tz} value={tz} />
                    ))}
                  </datalist>
                </div>
              </div>
            </section>

            {/* ── Appearance ────────────────────────────────────────── */}
            {/* Dark mode is hidden for now -- see DARK_MODE_AVAILABLE. */}
            {DARK_MODE_AVAILABLE && (
              <section className={CARD}>
                <h2 className={H2}>{field("appearance", "Appearance")}</h2>
                <label htmlFor={`${formId}-theme`} className={LABEL}>
                  {field("theme", "Theme")}
                </label>
                <select
                  id={`${formId}-theme`}
                  value={prefs.theme}
                  onChange={(e) => void handleThemeChange(e.target.value as UserTheme)}
                  className={`${INPUT} max-w-xs`}
                >
                  <option value="light">{field("themeLight", "Light")}</option>
                  <option value="dark">{field("themeDark", "Dark")}</option>
                  <option value="system">{field("themeSystem", "Match system")}</option>
                </select>
                <p className="text-xs text-muted-foreground mt-2">
                  {field("themeHint", "Updates the suite immediately and syncs with your account.")}
                </p>
              </section>
            )}

            {/* ── Date & time ──────────────────────────────────────── */}
            <section className={CARD}>
              <h2 className={H2}>{field("dateTime", "Date & time")}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor={`${formId}-date`} className={LABEL}>
                    {field("dateFormat", "Date format")}
                  </label>
                  <select
                    id={`${formId}-date`}
                    dir="ltr"
                    value={prefs.date_format}
                    onChange={(e) => updateLocal({ date_format: e.target.value as UserDateFormat })}
                    className={INPUT}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
                <div>
                  <label htmlFor={`${formId}-time`} className={LABEL}>
                    {field("timeFormat", "Time format")}
                  </label>
                  <select
                    id={`${formId}-time`}
                    value={prefs.time_format}
                    onChange={(e) => updateLocal({ time_format: e.target.value as UserTimeFormat })}
                    className={INPUT}
                  >
                    <option value="24h">{field("time24", "24-hour")}</option>
                    <option value="12h">{field("time12", "12-hour")}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor={`${formId}-week`} className={LABEL}>
                    {field("weekStart", "Start week on")}
                  </label>
                  <select
                    id={`${formId}-week`}
                    value={prefs.start_of_week}
                    onChange={(e) => updateLocal({ start_of_week: e.target.value as UserStartOfWeek })}
                    className={`${INPUT} max-w-xs`}
                  >
                    <option value="sunday">{field("sunday", "Sunday")}</option>
                    <option value="monday">{field("monday", "Monday")}</option>
                    <option value="saturday">{field("saturday", "Saturday")}</option>
                  </select>
                </div>
              </div>
            </section>

            {/* ── Notifications ────────────────────────────────────── */}
            <section className={CARD}>
              <h2 className={H2}>{field("notifications", "Notifications")}</h2>
              <div className="space-y-2">
                {(
                  [
                    ["notifications_email", "notifEmail", "Email"],
                    ["notifications_push", "notifPush", "Push"],
                    ["notifications_in_app", "notifInApp", "In-app"],
                  ] as const
                ).map(([key, labelKey, label]) => (
                  <label key={key} className={CHECK_ROW}>
                    <span className="text-sm font-semibold">{field(labelKey, label)}</span>
                    <input
                      type="checkbox"
                      checked={prefs[key]}
                      onChange={(e) => updateLocal({ [key]: e.target.checked })}
                      className={CHECKBOX}
                    />
                  </label>
                ))}
                <div className="pt-3">
                  <label htmlFor={`${formId}-digest`} className={LABEL}>
                    {field("digest", "Digest frequency")}
                  </label>
                  <select
                    id={`${formId}-digest`}
                    value={prefs.notification_digest}
                    onChange={(e) =>
                      updateLocal({
                        notification_digest: e.target.value as UserNotificationDigest,
                      })
                    }
                    className={`${INPUT} max-w-xs`}
                  >
                    <option value="instant">{field("digestInstant", "Instant")}</option>
                    <option value="daily">{field("digestDaily", "Daily")}</option>
                    <option value="weekly">{field("digestWeekly", "Weekly")}</option>
                    <option value="off">{field("digestOff", "Off")}</option>
                  </select>
                </div>
              </div>
            </section>

            {/* ── Accessibility ────────────────────────────────────── */}
            <section className={CARD}>
              <h2 className={H2}>{field("accessibility", "Accessibility")}</h2>
              <div className="space-y-2 mb-4">
                <label className={CHECK_ROW}>
                  <span className="text-sm font-semibold">{field("reduceMotion", "Reduce motion")}</span>
                  <input
                    type="checkbox"
                    checked={prefs.accessibility_reduce_motion}
                    onChange={(e) => updateLocal({ accessibility_reduce_motion: e.target.checked })}
                    className={CHECKBOX}
                  />
                </label>
                <label className={CHECK_ROW}>
                  <span className="text-sm font-semibold">
                    {field("highContrast", "High contrast (where supported)")}
                  </span>
                  <input
                    type="checkbox"
                    checked={prefs.accessibility_high_contrast}
                    onChange={(e) => updateLocal({ accessibility_high_contrast: e.target.checked })}
                    className={CHECKBOX}
                  />
                </label>
              </div>
              <label htmlFor={`${formId}-font`} className={LABEL}>
                {field("fontSize", "Font size")}
              </label>
              <select
                id={`${formId}-font`}
                value={prefs.accessibility_font_size}
                onChange={(e) =>
                  updateLocal({
                    accessibility_font_size: e.target.value as UserAccessibilityFontSize,
                  })
                }
                className={`${INPUT} max-w-xs`}
              >
                <option value="small">{field("fontSmall", "Small")}</option>
                <option value="medium">{field("fontMedium", "Medium")}</option>
                <option value="large">{field("fontLarge", "Large")}</option>
              </select>
            </section>

            {/* ── Keyboard shortcuts ───────────────────────────────── */}
            <section id="keyboard-shortcuts" className={`${CARD} scroll-mt-24`}>
              <h2 className="text-lg font-bold mb-1 text-start">{field("shortcuts", "Keyboard shortcuts")}</h2>
              <p className="text-sm text-muted-foreground mb-4 text-start">
                {field("shortcutsHint", "Global shortcuts when using the We-Co Suite shell.")}
              </p>
              <label className={`${CHECK_ROW} mb-4`}>
                <span className="text-sm font-semibold">{field("shortcutsEnable", "Enable keyboard shortcuts")}</span>
                <input
                  type="checkbox"
                  checked={prefs.keyboard_shortcuts_enabled}
                  onChange={(e) => updateLocal({ keyboard_shortcuts_enabled: e.target.checked })}
                  className={CHECKBOX}
                />
              </label>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex justify-between gap-4">
                  <span>{field("openSearch", "Open search")}</span>
                  <kbd dir="ltr" className="px-2 py-0.5 rounded-md text-xs bg-muted border border-border text-foreground">
                    ⌘ K
                  </kbd>
                </li>
                {DARK_MODE_AVAILABLE && (
                  <li className="flex justify-between gap-4">
                    <span>{field("toggleTheme", "Toggle theme")}</span>
                    <span className="text-xs">
                      {field("toggleThemeHint", "Use the Appearance control in your profile menu")}
                    </span>
                  </li>
                )}
              </ul>
            </section>

            {error && (
              <p role="alert" className="text-sm px-4 py-3 rounded-2xl bg-red-500/10 text-red-600 dark:text-red-400">
                {error}
              </p>
            )}
          </>
        )}

        {!loading && !prefs && error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>

      {toast && (
        <Toast message={toast.message} type={toast.type} duration={4000} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

const CARD = "bg-background border border-border p-6 max-md:p-4 rounded-3xl shadow-sm";
const H2 = "text-lg font-bold mb-4 text-start";
const LABEL = "block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 text-start";
const INPUT =
  "w-full h-10 px-3 rounded-xl text-sm border border-border bg-muted/40 outline-none focus:bg-background focus:border-primary transition-colors";
const CHECK_ROW =
  "flex items-center justify-between gap-3 p-4 rounded-2xl bg-muted/30 border border-border cursor-pointer";
const CHECKBOX = "size-4 shrink-0 accent-primary cursor-pointer";
const BTN_OUTLINE =
  "px-4 h-10 rounded-2xl border border-border text-sm font-bold hover:bg-muted transition-colors";

export default PreferencesPage;
